"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from "react";

interface EncryptionPasswordContextType {
  /** The encryption password (stored in memory only) */
  password: string | null;
  /** Set the encryption password */
  setPassword: (password: string) => void;
  /** Clear the encryption password from memory */
  clearPassword: () => void;
  /** Check if a password is currently set */
  hasPassword: boolean;
  /** Whether user has been prompted for password this session */
  hasBeenPrompted: boolean;
  /** Mark that user has been prompted */
  markPrompted: () => void;
  /** Reset prompted state (for re-prompting) */
  resetPrompted: () => void;
}

const EncryptionPasswordContext =
  createContext<EncryptionPasswordContextType | null>(null);

export function EncryptionPasswordProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [password, setPasswordState] = useState<string | null>(null);
  const [hasBeenPrompted, setHasBeenPrompted] = useState(false);

  const setPassword = useCallback((newPassword: string) => {
    setPasswordState(newPassword);
  }, []);

  const clearPassword = useCallback(() => {
    setPasswordState(null);
    setHasBeenPrompted(false);
  }, []);

  const markPrompted = useCallback(() => {
    setHasBeenPrompted(true);
  }, []);

  const resetPrompted = useCallback(() => {
    setHasBeenPrompted(false);
  }, []);

  const value: EncryptionPasswordContextType = {
    password,
    setPassword,
    clearPassword,
    hasPassword: password !== null && password.length > 0,
    hasBeenPrompted,
    markPrompted,
    resetPrompted,
  };

  return (
    <EncryptionPasswordContext.Provider value={value}>
      {children}
    </EncryptionPasswordContext.Provider>
  );
}

export function useEncryptionPassword(): EncryptionPasswordContextType {
  const context = useContext(EncryptionPasswordContext);
  if (!context) {
    throw new Error(
      "useEncryptionPassword must be used within EncryptionPasswordProvider",
    );
  }
  return context;
}
